import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, NgZone, ElementRef } from '@angular/core';
import { CmsBannerCarouselComponent, ProductService, CmsService } from '@spartacus/core';
import { BannerCarouselComponent, CarouselModule, CmsComponentData, MediaModule, PageComponentModule } from '@spartacus/storefront';
import { fromEvent, Subject, merge, Subscription, interval, combineLatest } from 'rxjs';
import { map, filter, takeUntil, tap, switchMap, distinctUntilChanged } from 'rxjs/operators';
import { GenGaListNames } from 'src/app/generac/enums/ga-list-names.enum';
import { GenCustomerService } from 'src/app/generac/services/customer.service';
import { GenGoogleAnalyticsService } from 'src/app/generac/services/google-analytics.service';
import { isEqual } from 'src/app/generac/shared/helpers/is-equal';

@Component({
  selector: 'gen-banner-carousel',
  templateUrl: './banner-carousel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, PageComponentModule, CarouselModule, MediaModule],
})
export class GenBannerCarouselComponent extends BannerCarouselComponent implements OnInit, OnDestroy {
  private readonly GA_HOMEPAGE_CREATIVE_SLOT_NAME = 'homepage_top';

  private unsubscribe$ = new Subject();
  private autoplaySub?: Subscription;
  private lastTimeout = 0;
  private carouselElement!: HTMLElement;

  constructor(
    protected _componentData: CmsComponentData<CmsBannerCarouselComponent>,
    protected _cmsService: CmsService,
    protected productService: ProductService,
    protected customerService: GenCustomerService,
    private ngZone: NgZone,
    private elRef: ElementRef,
    private googleAnalyticsService: GenGoogleAnalyticsService,
  ) {
    super(_componentData, _cmsService);
  }

  ngOnInit(): void {
    this.carouselElement = this.elRef.nativeElement;

    this.handleCarouselEvents();
    this.handleComponentData();
  }

  private handleComponentData(): void {
    merge(
      fromEvent(window, 'pauseCarousel').pipe(map(() => 'pause')),
      fromEvent(window, 'resumeCarousel').pipe(map(() => 'resume'))
    )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(action => {
        if (action === 'pause') {
          this.stopAutoPlay();
        } else {
          this.startAutoPlay(this.lastTimeout);
        }
      });
  }

  private handleCarouselEvents(): void {
    combineLatest([merge(
      this._componentData.data$,
      this.customerService.getB2bUnitChanged().pipe(
        map(() =>
          this._cmsService.refreshComponent(this._componentData.uid, {
            id: 'current',
          })
        )
      )),
    this.getItems()
    ]).pipe(
      filter(([config, _]) => !!config),
      takeUntil(this.unsubscribe$),
      tap(([config, _]) => {
        const timeoutSec: any = Number((config as any)?.timeout);
        if (timeoutSec > 0 && timeoutSec) {
          const timeoutMs = timeoutSec * 1000;
          this.lastTimeout = timeoutMs;
          this.startAutoPlay(timeoutMs);
        } else {
          this.stopAutoPlay();
        }
      }),
      switchMap(([_, items]) => combineLatest(items)),
      distinctUntilChanged(isEqual),
      tap((items: any) => {
        items.forEach((item: any, index: number) => {
          this.googleAnalyticsService.sendGaEvent("view_promotion", {
            items: [{
              item_id: item.name.toLowerCase().trim().replace(/ /g, '_'),
              item_name: item.name,
              creative_slot: this.GA_HOMEPAGE_CREATIVE_SLOT_NAME,
              index: index,
              item_list_name: GenGaListNames.HOMEPAGE_BANNER_CAROUSEL,
              item_list_id: GenGaListNames.HOMEPAGE_BANNER_CAROUSEL.toLowerCase().trim().replace(/ /g, '_'),
            }]
          });
        });
      })
    ).subscribe();
  }

  private startAutoPlay(timeout: number): void {
    this.stopAutoPlay();

    if (timeout <= 0 || !this.carouselElement) return;

    const nextBtn = this.carouselElement.querySelector<HTMLButtonElement>('button.next');
    const firstDot = this.carouselElement.querySelector<HTMLButtonElement>('.indicators button:first-child');

    this.ngZone.runOutsideAngular(() => {
      this.autoplaySub = interval(timeout)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(() => {
          const rightDisabled = nextBtn?.getAttribute('aria-disabled') === 'true';

          if (nextBtn && !rightDisabled) {
            nextBtn.click();
          } else if (firstDot) {
            firstDot.click();
          }
        });
    });
  }

  private stopAutoPlay(): void {
    this.autoplaySub?.unsubscribe();
    this.autoplaySub = undefined;
  }

  ngOnDestroy(): void {
    this.autoplaySub?.unsubscribe();
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }
}